import Nango from '@nangohq/frontend';
import { api } from '../api';
import { nangoLogger } from '../../utils/loggerInstance';
import { withRetry, defaultRetryConfig, RetryConfig } from '../../utils/apiRetry';
import {
  getApiErrorCode,
  getApiErrorMessage,
  isQuotaExceeded429Error,
} from '../../utils/apiErrorUtils';
import { reportAnalyticsError, AnalyticsErrorContext } from '../../utils/analyticsErrorReporting';
import { securityEventTracker } from '../securityEventTracker';
import { ENV } from '../../config/env';
import { AUTH_CONFIG } from '../../config/auth';
import authService from '../authService';
import { NangoConnection } from '../../types';
import { enableBodyPointerEvents, restoreBodyPointerEvents } from '../../utils/iframeFocusHelper';
import { slackChannelsResponseSchema, SlackChannelsResponse } from '../../schemas/slack';
import {
  availableProviderConfigsResponseSchema,
  AvailableProviderConfigsResponse,
} from '../../schemas/nango';
import { PROVIDERS_REQUIRING_POST } from '../../components/CloudExportDialog/definitions';
import { isConnectionLimitError, CONNECTION_LIMIT_ERROR_MESSAGE } from '../../utils/connections';

const logger = nangoLogger;

// Provider configuration mapping
const PROVIDER_CONFIG_MAP = {
  'google-drive': 'google-drive',
  'one-drive': 'one-drive',
  dropbox: 'dropbox',
  box: 'box',
  hubspot: 'hubspot',
  confluence: 'confluence',
  notion: 'notion',
  snowflake: 'snowflake',
  servicenow: 'servicenow',
  'google-cloud-storage': 'google-cloud-storage',
  zoho: 'zoho',
  signnow: 'signnow',
  quickbooks: 'quickbooks',
  slack: 'slack',
  trello: 'trello',
} as const;

export type SupportedProvider = keyof typeof PROVIDER_CONFIG_MAP;
const API_BASE_URL = AUTH_CONFIG.API_BASE_URL;

// Self-hosted Nango configuration
const NANGO_CONNECT_URL = ENV.VITE_NANGO_CONNECT_URL;
const NANGO_API_URL = ENV.VITE_NANGO_API_URL;

if (API_BASE_URL === undefined || API_BASE_URL === null) {
  throw new Error(
    'API_BASE_URL configuration is missing. Please check your AUTH_CONFIG to enable integrations.',
  );
}

if (!NANGO_CONNECT_URL || !NANGO_API_URL) {
  logger.warn('Self-hosted Nango URLs not configured, falling back to default behavior', {
    connectUrl: NANGO_CONNECT_URL,
    apiUrl: NANGO_API_URL,
  });
}

/**
 * Nango-specific retry configuration following analyticsApiService patterns
 */
const nangoRetryConfig: RetryConfig = {
  ...defaultRetryConfig,
  maxRetries: 2,
  baseDelay: 1500,
  shouldRetry: (error: Error | unknown) => {
    const status = (error as { response?: { status?: number; data?: unknown } })?.response?.status;
    if (status === 429 && isQuotaExceeded429Error(error)) {
      const data = (error as { response?: { data?: unknown } }).response?.data;
      logger.info('Nango retry skipped: HTTP 429 classified as quota/plan limit', {
        apiErrorCode: getApiErrorCode(data),
        apiMessage: getApiErrorMessage(data),
      });
      return false;
    }
    return status != null && (status >= 500 || status === 429);
  },
};

/**
 * Service for handling Nango integrations with enhanced user ID management
 * Updated to use self-hosted Nango instance following analyticsApiService patterns
 */
export class NangoService {
  private static instance: NangoService;
  private tempUserId: string | null = null;
  private lastAuthenticatedUserId: string | null = null;
  private authStateListener: ((userId: string | null) => void) | null = null;

  private constructor() {
    logger.debug(
      'NangoService initialized with hybrid user ID management and self-hosted configuration',
      {
        connectUrl: NANGO_CONNECT_URL,
        apiUrl: NANGO_API_URL,
      },
    );
    this.initializeTempUserId();
  }

  /**
   * Get singleton instance of NangoService
   * @returns NangoService instance
   */
  static getInstance(): NangoService {
    if (!NangoService.instance) {
      NangoService.instance = new NangoService();
    }
    return NangoService.instance;
  }

  private initializeTempUserId(): void {
    this.tempUserId = localStorage.getItem('tempUserId');
    if (this.tempUserId) {
      logger.debug('Restored temporary user ID from storage', { tempUserId: this.tempUserId });
    }
  }

  /**
   * Handle Nango API errors following analyticsApiService patterns
   */
  private handleNangoError(
    operation: string,
    error: Error | unknown,
    provider: SupportedProvider = 'google-drive',
  ): never {
    const errorObj = error as any;
    const status = errorObj?.response?.status;
    const data = errorObj?.response?.data;
    const message =
      getApiErrorMessage(data) ||
      (typeof data?.message === 'string' ? data.message : undefined) ||
      errorObj?.message;
    const retryAfter = errorObj?.response?.headers?.['retry-after'];

    // Report error for tracking
    const errorContext: AnalyticsErrorContext = {
      operation,
      component: 'nangoService',
      additionalData: {
        provider,
        status,
        nangoOperation: true,
        retryAfter,
      },
    };

    reportAnalyticsError(error, errorContext);

    // Create specific error types based on status codes following analyticsApiService patterns
    if (status === 429) {
      const waitTime = retryAfter ? Number.parseInt(retryAfter, 10) : 300; // Default 5 minutes
      const rateLimitError = new Error(
        `Too many authentication requests. Please wait ${Math.ceil(waitTime / 60)} minutes before trying again.`,
      );
      (rateLimitError as any).type = 'RATE_LIMIT';
      (rateLimitError as any).statusCode = 429;
      (rateLimitError as any).retryAfter = waitTime;
      throw rateLimitError;
    }

    if (status === 403) {
      const permissionError = new Error(
        `Access denied for ${provider}. Please grant the necessary permissions when prompted.`,
      );
      (permissionError as any).type = 'PERMISSION_DENIED';
      (permissionError as any).statusCode = 403;
      throw permissionError;
    }

    if (status === 401) {
      const authError = new Error(
        `Authentication required for ${provider}. Please reconnect your account.`,
      );
      (authError as any).type = 'AUTHENTICATION_REQUIRED';
      (authError as any).statusCode = 401;
      throw authError;
    }

    if (status === 400) {
      // Use the actual error message from backend
      // Preserve specific error messages like "Reached maximum number of allowed connections"
      let validationMessage = message || 'Invalid request parameters.';

      // Check for connection limit errors first - use generic message
      if (isConnectionLimitError(message)) {
        // Use generic message to avoid exposing technical details
        validationMessage = CONNECTION_LIMIT_ERROR_MESSAGE;
      } else if (message?.includes('connection') || message?.includes('invalid')) {
        // Only use generic message for other connection-related errors
        validationMessage = `Invalid connection configuration for ${provider}. Please try reconnecting.`;
      }

      // Log original error details server-side only for debugging
      logger.error('Validation error in Nango service', {
        operation,
        provider,
        message: validationMessage,
        originalMessage: message,
        status: 400,
        // Log response details server-side only, not attached to thrown error
        responseStatus: errorObj?.response?.status,
        responseData: errorObj?.response?.data,
      });

      const validationError = new Error(validationMessage);
      (validationError as any).type = 'VALIDATION_ERROR';
      (validationError as any).statusCode = 400;
      throw validationError;
    }

    if (status === 404) {
      const notFoundError = new Error(
        `${provider} service endpoint not found. This feature may not be available.`,
      );
      (notFoundError as any).type = 'NOT_FOUND';
      (notFoundError as any).statusCode = 404;
      throw notFoundError;
    }

    if (status >= 500) {
      let serverMessage = `${provider} service temporarily unavailable. Please try again in a few minutes.`;

      if (status === 503) {
        serverMessage = `${provider} service is under maintenance. Please try again later.`;
      } else if (status === 502) {
        serverMessage = `${provider} service gateway error. Please try again.`;
      }

      const serverError = new Error(serverMessage);
      (serverError as any).type = 'SERVER_ERROR';
      (serverError as any).statusCode = status;
      throw serverError;
    }

    // Network errors (no response)
    if (!errorObj?.response && errorObj?.request) {
      const networkError = new Error(
        `Unable to connect to ${provider} service. Please check your internet connection.`,
      );
      (networkError as any).type = 'NETWORK_ERROR';
      (networkError as any).statusCode = 0;
      throw networkError;
    }

    // Unknown errors
    const unknownError = new Error(
      message ||
        `An unexpected error occurred during ${operation} with ${provider}. Please try again.`,
    );
    (unknownError as any).type = 'UNKNOWN_ERROR';
    (unknownError as any).statusCode = status || 0;
    throw unknownError;
  }

  /**
   * Get current user ID using hybrid approach
   * Priority: Authenticated User ID > Existing Temp ID > New Temp ID
   * @returns User ID
   */
  private getCurrentUserId(): string {
    // Priority 1: Authenticated user ID
    const authenticatedId = this.getAuthenticatedUserId();
    if (authenticatedId) {
      logger.debug('Using authenticated user ID', { userId: authenticatedId });
      return authenticatedId;
    }

    // Priority 2: Existing temporary ID
    if (this.tempUserId) {
      logger.debug('Using existing temporary user ID', { tempUserId: this.tempUserId });
      return this.tempUserId;
    }

    // Priority 3: Generate new temporary ID
    this.tempUserId = this.generateTemporaryUserId();
    localStorage.setItem('tempUserId', this.tempUserId);
    logger.info('Generated new temporary user ID', { tempUserId: this.tempUserId });

    return this.tempUserId;
  }

  /**
   * Get authenticated user ID from multiple sources
   * @returns Authenticated user ID or null
   */
  private getAuthenticatedUserId(): string | null {
    // Try multiple sources for authenticated user ID
    const sources = [
      this.getBackendUserIdFromAuth(), // Prioritize auth service
      localStorage.getItem('userId'),
      sessionStorage.getItem('userId'),
      this.getSupabaseUserId(),
    ];

    logger.debug('🔍 [NangoService] getAuthenticatedUserId sources:', {
      authService: this.getBackendUserIdFromAuth(),
      localStorage: localStorage.getItem('userId'),
      sessionStorage: sessionStorage.getItem('userId'),
      supabase: this.getSupabaseUserId(),
    });

    for (const source of sources) {
      if (source) {
        logger.debug('Found authenticated user ID from source', { userId: source });
        return source;
      }
    }

    return null;
  }

  /**
   * Get backend user ID from auth context if available
   * @returns Backend user ID or null
   */
  public getBackendUserIdFromAuth(): string | null {
    try {
      // Priority 1: Try to get from auth context on window (most up-to-date)
      const authContext = (window as any).__AUTH_CONTEXT__;
      if (authContext?.getBackendUserId) {
        const userId = authContext.getBackendUserId();
        if (userId) return userId;
      }
      if (authContext?.backendUserId) {
        return authContext.backendUserId;
      }

      // Priority 2: Use authService.getBackendUserId() like other services do
      const userId = authService.getBackendUserId();
      if (userId) return userId;

      return null;
    } catch (error) {
      logger.debug('Failed to get backend user ID from auth service', { error });
      return null;
    }
  }

  /**
   * Get Supabase user ID if available
   * @returns Supabase user ID or null
   */
  private getSupabaseUserId(): string | null {
    try {
      // Try to get from Supabase auth if available
      // Note: Supabase manages its own session, tokens are not in localStorage
      const supabaseAuth = (window as any).supabase?.auth?.session();
      if (supabaseAuth?.user?.id) {
        return supabaseAuth.user.id;
      }

      // Supabase tokens are managed by Supabase client, not accessible via localStorage
      return null;
    } catch (error) {
      logger.debug('Failed to get Supabase user ID', { error: String(error) });
      return null;
    }
  }

  /**
   * Generate a temporary user ID with session consistency
   * @returns Temporary user ID
   */
  private generateTemporaryUserId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);

    // Use existing session ID or generate new one
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = Math.random().toString(36).substring(2, 9);
      sessionStorage.setItem('sessionId', sessionId);
    }

    // Include browser fingerprint for better consistency
    const fingerprint = this.getBrowserFingerprint();

    return `temp-${timestamp}-${random}-${sessionId}-${fingerprint}`;
  }

  /**
   * Generate a simple browser fingerprint for consistency
   * @returns Browser fingerprint string
   */
  private getBrowserFingerprint(): string {
    try {
      const fingerprint = [
        navigator.userAgent.slice(0, 20),
        navigator.language,
        screen.width + 'x' + screen.height,
        new Date().getTimezoneOffset(),
      ].join('|');

      // Create a simple hash
      let hash = 0;
      for (let i = 0; i < fingerprint.length; i++) {
        const char = fingerprint.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32-bit integer
      }

      return Math.abs(hash).toString(36).slice(0, 8);
    } catch (error) {
      logger.debug('Failed to generate browser fingerprint', { error: String(error) });
      return 'unknown';
    }
  }

  /**
   * Handle user authentication event
   * Migrates temporary connections to authenticated user
   * @param authenticatedUserId - The authenticated user ID
   */
  public onUserAuthenticated(authenticatedUserId: string): void {
    logger.debug('🔐 [NangoService] onUserAuthenticated called:', {
      authenticatedUserId,
      tempUserId: this.tempUserId,
      lastAuthenticatedUserId: this.lastAuthenticatedUserId,
    });

    // Prevent repeated calls for the same user
    if (this.lastAuthenticatedUserId === authenticatedUserId) {
      logger.debug('🔄 [NangoService] User already authenticated, skipping migration check');
      return;
    }

    this.lastAuthenticatedUserId = authenticatedUserId;

    logger.info('User authenticated, checking for migration', {
      authenticatedUserId,
      tempUserId: this.tempUserId,
    });

    if (this.tempUserId && this.tempUserId !== authenticatedUserId) {
      logger.info('🔄 [NangoService] Migrating connections from temporary to authenticated user', {
        from: this.tempUserId,
        to: authenticatedUserId,
      });

      // Migrate connections
      this.migrateConnections(this.tempUserId, authenticatedUserId);

      // Clean up temporary ID
      localStorage.removeItem('tempUserId');
      this.tempUserId = null;

      logger.info('✅ [NangoService] Migration completed successfully');
    } else if (this.tempUserId === authenticatedUserId) {
      logger.debug('🔄 [NangoService] Temporary ID matches authenticated ID, no migration needed');
    } else {
      logger.info('ℹ️ [NangoService] No temporary user ID found, no migration needed');
    }
  }

  /**
   * Migrate connections from temporary to authenticated user
   * @param tempUserId - Temporary user ID
   * @param authenticatedUserId - Authenticated user ID
   */
  private async migrateConnections(tempUserId: string, authenticatedUserId: string): Promise<void> {
    try {
      // Call backend to migrate connections
      await api.post('/api/v1/nango/migrate-connections', {
        tempUserId,
        authenticatedUserId,
      });

      logger.info('Successfully migrated connections on backend');
    } catch (error) {
      logger.error('Failed to migrate connections on backend', error);
      // Don't throw - migration failure shouldn't break the app
    }
  }

  /**
   * Set up auth state listener for automatic migration
   * @param listener - Function to call when auth state changes
   */
  public setAuthStateListener(listener: (userId: string | null) => void): void {
    this.authStateListener = listener;
    logger.debug('Auth state listener set');
  }

  /**
   * Notify auth state change (called by auth system)
   * @param userId - New user ID or null if logged out
   */
  public notifyAuthStateChange(userId: string | null): void {
    if (this.authStateListener) {
      this.authStateListener(userId);
    }

    if (userId) {
      this.onUserAuthenticated(userId);
    } else {
      // User logged out, but keep temp ID for continuity
      logger.debug('User logged out, keeping temporary ID for continuity');
    }
  }

  /**
   * Get user ID type for debugging
   * @returns Object describing the current user ID state
   */
  public getUserIDInfo(): {
    userId: string;
    type: 'authenticated' | 'temporary' | 'new-temporary';
    source: string;
    tempUserId?: string;
  } {
    const authenticatedId = this.getAuthenticatedUserId();

    if (authenticatedId) {
      return {
        userId: authenticatedId,
        type: 'authenticated',
        source: 'auth-system',
      };
    }

    if (this.tempUserId) {
      return {
        userId: this.tempUserId,
        type: 'temporary',
        source: 'existing-temp',
        tempUserId: this.tempUserId,
      };
    }

    // Generate new temp ID for info
    const newTempId = this.generateTemporaryUserId();
    return {
      userId: newTempId,
      type: 'new-temporary',
      source: 'generated',
      tempUserId: newTempId,
    };
  }

  /**
   * Get provider config key for a given provider
   * @param provider - Provider name
   * @returns Provider config key
   */
  private getProviderConfigKey(provider: SupportedProvider): string {
    return PROVIDER_CONFIG_MAP[provider];
  }

  /**
   * Create Nango instance with self-hosted configuration
   */
  private createNangoInstance(connectSessionToken?: string): Nango {
    const config: Record<string, unknown> = {};

    // Use self-hosted URLs if configured
    if (NANGO_CONNECT_URL) {
      config.baseURL = NANGO_CONNECT_URL;
    }
    if (NANGO_API_URL) {
      config.apiURL = NANGO_API_URL;
    }

    // Add session token if provided
    if (connectSessionToken) {
      config.connectSessionToken = connectSessionToken;
    }

    logger.debug('Creating Nango instance with configuration', {
      baseURL: config.baseURL,
      apiURL: config.apiURL,
      hasSessionToken: !!connectSessionToken,
    });

    return new Nango(config);
  }

  /**
   * Fetch connect session token from backend with retry logic
   * @param userId - User ID for the connection (optional, uses current user if not provided)
   * @param provider - Provider name (optional, defaults to google-drive)
   * @returns Connect session token
   */
  async getConnectSessionToken(
    userId?: string,
    provider: SupportedProvider = 'google-drive',
  ): Promise<string> {
    const effectiveUserId = userId || this.getCurrentUserId();

    try {
      const result = await withRetry(
        async () => {
          logger.debug('Fetching connect session token', { userId: effectiveUserId, provider });

          const response = await api.post('/api/v1/nango/connect-session-token', {
            user_id: effectiveUserId,
            end_user_id: effectiveUserId,
            provider: provider,
          });
          return response.data.connectSessionToken;
        },
        nangoRetryConfig,
        (attempt, delay, error) => {
          logger.warn(`Retrying getConnectSessionToken (attempt ${attempt})`, {
            delay,
            error: error instanceof Error ? error.message : String(error),
            userId: effectiveUserId,
          });
        },
      );

      return result;
    } catch (error) {
      return this.handleNangoError('getConnectSessionToken', error, provider);
    }
  }

  /**
   * Common method to handle OAuth connection for any provider
   * @param provider - Provider name
   * @param userId - User ID for the connection (optional, uses current user if not provided)
   * @returns Promise that resolves with connection details when connection is established
   */
  private async connectProvider(
    provider: SupportedProvider,
    userId?: string,
  ): Promise<{ connectionId: string; providerConfigKey: string }> {
    const effectiveUserId = userId || this.getCurrentUserId();
    const startTime = performance.now();

    logger.info(`🚀 [NangoService] Starting ${provider} connection...`, {
      userId: effectiveUserId,
    });

    try {
      logger.info(`Connecting ${provider}`, { userId: effectiveUserId });

      logger.debug('🔑 [NangoService] Getting connect session token...');
      const connectSessionToken = await this.getConnectSessionToken(effectiveUserId, provider);
      logger.debug('✅ [NangoService] Connect session token received');

      logger.debug('🏗️ [NangoService] Creating Nango instance...');
      const nango = this.createNangoInstance(connectSessionToken);
      logger.debug('✅ [NangoService] Nango instance created');

      // Return a Promise that resolves when connection is established
      logger.info('🎯 [NangoService] Opening Connect UI and waiting for connection...');

      // Ensure the Nango iframe is interactable even if a parent modal has locked body pointer-events
      enableBodyPointerEvents();

      return new Promise((resolve, reject) => {
        let connectionEstablished = false;

        const connect = nango.openConnectUI({
          onEvent: async (event: any) => {
            logger.debug('📡 [NangoService] Received event:', { type: event.type, event });

            if (event.type === 'connect') {
              logger.info(`🎉 [NangoService] CONNECTION SUCCESS! ${provider} connected!`, {
                connectionId: event.payload.connectionId,
                providerConfigKey: event.payload.providerConfigKey,
                userId: effectiveUserId,
              });

              // Mark connection as established immediately - OAuth was successful
              connectionEstablished = true;

              try {
                // Save connection to database and wait for completion
                logger.debug('💾 [NangoService] Saving connection to database...');
                await this.saveConnection(
                  event.payload.connectionId,
                  event.payload.providerConfigKey,
                  effectiveUserId,
                  provider,
                );
                logger.debug('✅ [NangoService] Connection saved to database');

                // Track performance
                securityEventTracker.trackPerformance(`${provider}-oauth`, startTime, true, {
                  provider: provider,
                  userId: effectiveUserId,
                });

                // Resolve with connection details
                logger.debug('🎯 [NangoService] Resolving Promise with connection details...');
                resolve({
                  connectionId: event.payload.connectionId,
                  providerConfigKey: event.payload.providerConfigKey,
                });
                logger.debug('✅ [NangoService] Promise resolved!');
              } catch (saveError) {
                logger.error('💥 [NangoService] Failed to save connection:', {
                  error: saveError,
                  provider,
                  connectionId: event.payload.connectionId,
                  userId: effectiveUserId,
                });

                // Still resolve the promise since OAuth was successful
                // The connection exists in Nango, just not saved to our database yet
                logger.warn(
                  '⚠️ [NangoService] Resolving despite save failure - OAuth was successful',
                );
                resolve({
                  connectionId: event.payload.connectionId,
                  providerConfigKey: event.payload.providerConfigKey,
                });
              }
            } else if (event.type === 'close') {
              restoreBodyPointerEvents();
              if (connectionEstablished) {
                // Connection was already established, this is expected behavior
                logger.debug(
                  '✅ [NangoService] Connect window closed after successful connection - this is expected',
                  {
                    provider,
                    userId: effectiveUserId,
                  },
                );
                // Don't reject - the connection was already established and resolved
                return;
              } else {
                // User closed window before completing OAuth
                logger.warn(
                  '❌ [NangoService] User closed the connect window before completing OAuth',
                  {
                    provider,
                    userId: effectiveUserId,
                  },
                );
                reject(
                  new Error('User closed the connect window before completing authentication'),
                );
              }
            } else if (event.type === 'error') {
              const errorMessage = event.payload?.message || 'OAuth flow error';
              logger.error('💥 [NangoService] OAuth error occurred:', {
                errorMessage,
                error: event.payload,
                provider,
                userId: effectiveUserId,
              });

              reject(new Error(errorMessage));
            }
          },
        });

        // Set session token
        try {
          logger.debug('🔐 [NangoService] Setting session token...');
          connect.setSessionToken(connectSessionToken);
          logger.debug('✅ [NangoService] Session token set successfully');
        } catch (error) {
          logger.error('💥 [NangoService] Failed to set session token:', error);
          reject(error);
        }
      });
    } catch (error) {
      logger.error(`💥 [NangoService] Exception in connect${provider}:`, error);

      // Track OAuth failure

      // Track performance for failed operation
      securityEventTracker.trackPerformance(`${provider}-oauth`, startTime, false, {
        provider: provider,
        userId: effectiveUserId,
        error: error instanceof Error ? error.message : String(error),
      });

      throw this.handleNangoError(`connect${provider}`, error, provider);
    }
  }

  /**
   * Open Nango Connect UI for Google Drive with enhanced error handling and event tracking
   * @param userId - User ID for the connection (optional, uses current user if not provided)
   * @returns Promise that resolves with connection details when connection is established
   */
  async connectGoogleDrive(
    userId?: string,
  ): Promise<{ connectionId: string; providerConfigKey: string }> {
    return this.connectProvider('google-drive', userId);
  }

  /**
   * Common method to handle response format checking for connection status
   * @param responseData - Response data from API
   * @returns Object containing connected status and connection ID
   */
  private parseConnectionStatus(responseData: any): {
    connected: boolean;
    connectionId: string | null;
  } {
    logger.debug(`🔍 [NangoService] parseConnectionStatus called with:`, {
      responseData,
      type: typeof responseData,
      isObject: responseData && typeof responseData === 'object',
    });

    let connected = false;
    let connectionId: string | null = null;

    // Handle different response formats
    if (responseData && typeof responseData === 'object') {
      logger.debug(`🔍 [NangoService] Response is object, checking fields:`, {
        hasConnected: 'connected' in responseData,
        connectedValue: responseData.connected,
        hasStatus: 'status' in responseData,
        statusValue: responseData.status,
        hasConnectionId: 'connectionId' in responseData,
        connectionIdValue: responseData.connectionId,
        hasId: 'id' in responseData,
        idValue: responseData.id,
      });

      // Backend now ensures consistency, so we can trust the connected field
      if ('connected' in responseData) {
        connected = responseData.connected;
      }
      // Fallback: check status field for backward compatibility
      else if ('status' in responseData) {
        connected = responseData.status === 'connected';
      }
      // If response exists and has data, consider it connected
      else if (responseData.connectionId || responseData.id) {
        connected = true;
      }

      // Extract connection ID
      if (responseData.connectionId) {
        connectionId = responseData.connectionId;
      } else if (responseData.connection_id) {
        connectionId = responseData.connection_id;
      } else if (responseData.id) {
        connectionId = responseData.id;
      }
    }

    logger.debug(`🔍 [NangoService] Parsed connection status:`, {
      connected,
      connectionId,
    });

    return { connected, connectionId };
  }

  /**
   * Get effective user ID for API calls
   * Priority: provided userId > authenticatedUserId > currentUserId
   * @param userId - Optional user ID parameter
   * @returns Effective user ID to use
   */
  private getEffectiveUserId(userId?: string): string {
    const authenticatedUserId = this.getAuthenticatedUserId();
    const currentUserId = this.getCurrentUserId();
    return userId || authenticatedUserId || currentUserId;
  }

  /**
   * Helper to construct query parameters for connection endpoints
   * @param userId - User ID to include in query parameters
   * @returns URLSearchParams object with user_id parameter
   */
  private buildConnectionQueryParams(userId?: string): URLSearchParams {
    const effectiveUserId = this.getEffectiveUserId(userId);
    const params = new URLSearchParams();
    params.append('user_id', effectiveUserId);
    return params;
  }

  /**
   * Common method to check connection status for any provider
   * @param provider - Provider name
   * @param endpoint - API endpoint for checking connection
   * @returns Boolean indicating if connected
   */
  /**
   * Helper to execute a GET request with fallback to POST on 405 Method Not Allowed
   * This centralizes the logic for handling legacy vs new API endpoints
   */
  private async makeRequestWithFallback(
    provider: SupportedProvider,
    endpoint: string,
    userId?: string,
    method: 'GET' | 'POST' = 'GET',
  ) {
    const queryParams = this.buildConnectionQueryParams(userId);
    const fullUrl = `${endpoint}?${queryParams.toString()}`;

    logger.debug(`🌐 [NangoService] Making request to: ${fullUrl}`);

    try {
      if (method === 'POST') {
        const effectiveUserId = this.getEffectiveUserId(userId);
        return await api.post(endpoint, { user_id: effectiveUserId });
      }
      return await api.get(fullUrl);
    } catch (error: any) {
      if (error?.response?.status === 405) {
        logger.warn(`⚠️ [NangoService] GET not supported for ${provider}, falling back to POST`, {
          provider,
          endpoint,
        });
        const effectiveUserId = this.getEffectiveUserId(userId);
        return await api.post(endpoint, { user_id: effectiveUserId });
      }
      throw error;
    }
  }

  private async checkProviderConnection(
    provider: SupportedProvider,
    endpoint: string,
    userId?: string,
    method: 'GET' | 'POST' = 'GET',
  ): Promise<{ connected: boolean; connectionId: string | null }> {
    logger.debug(`🔍 [NangoService] checkProviderConnection called:`, {
      provider,
      endpoint,
      userId,
    });

    try {
      const result = await withRetry(
        async () => {
          const queryParams = this.buildConnectionQueryParams(userId);
          const fullUrl = `${endpoint}?${queryParams.toString()}`;

          logger.debug(`🔍 [NangoService] Checking ${provider} connection for user:`, {
            effectiveUserId: this.getEffectiveUserId(userId),
            endpoint: fullUrl,
          });

          const response = await this.makeRequestWithFallback(provider, endpoint, userId, method);

          // Debug logging to see what we're actually getting
          logger.debug(`📊 [NangoService] Backend response:`, {
            provider,
            status: response.status,
            statusText: response.statusText,
            data: response.data,
            headers: response.headers,
            userId: this.getEffectiveUserId(userId),
            endpoint: fullUrl,
          });

          // ServiceNow and Box return simplified format: { "connected": boolean, "connection_id": string | null }
          // Normalize it to match other providers' format for parseConnectionStatus
          let responseData = response.data;
          if (
            (provider === 'servicenow' || provider === 'box') &&
            responseData &&
            typeof responseData === 'object'
          ) {
            responseData = {
              connected: responseData.connected,
              connectionId: responseData.connection_id || responseData.connectionId,
            };
          }

          const parsedResult = this.parseConnectionStatus(responseData);
          logger.debug(`🔍 [NangoService] Parsed connection status result:`, {
            provider,
            rawData: response.data,
            parsedResult,
          });
          return parsedResult;
        },
        nangoRetryConfig,
        (attempt, delay, error) => {
          logger.warn(`Retrying is${provider}Connected (attempt ${attempt})`, {
            delay,
            error: error instanceof Error ? error.message : String(error),
          });
        },
      );

      // Track connection check

      logger.info(`✅ [NangoService] Connection check completed successfully:`, {
        provider,
        result,
      });
      return result || { connected: false, connectionId: null };
    } catch (error) {
      logger.error(`❌ [NangoService] Connection check failed:`, {
        provider,
        error: error instanceof Error ? error.message : String(error),
        errorType: typeof error,
        errorObject: error,
      });

      // Track failed connection check

      return { connected: false, connectionId: null };
    }
  }

  /**
   * Get connection status for any supported provider
   * @param provider - Provider to check
   * @param userId - Optional user ID
   * @returns Promise with connection status and ID
   */
  public async getConnectionStatus(
    provider: SupportedProvider,
    userId?: string,
  ): Promise<{ connected: boolean; connectionId: string | null }> {
    const endpoint = `/api/v1/nango/connections/check/${provider}`;
    // Use POST for providers that require it, otherwise use GET
    const method = PROVIDERS_REQUIRING_POST.includes(provider) ? 'POST' : 'GET';
    return this.checkProviderConnection(provider, endpoint, userId, method);
  }

  /**
   * Check if Google Drive is connected with retry logic and event tracking
   * @returns Boolean indicating if connected
   */
  async isGoogleDriveConnected(): Promise<boolean> {
    return (
      await this.checkProviderConnection(
        'google-drive',
        '/api/v1/nango/connections/check/google-drive',
        undefined,
        'POST',
      )
    ).connected;
  }

  /**
   * Common method to get connection ID for any provider
   * @param provider - Provider name
   * @param endpoint - API endpoint for getting connection ID
   * @returns Connection ID or null if not connected
   */
  private async getProviderConnectionId(
    provider: SupportedProvider,
    endpoint: string,
    userId?: string,
  ): Promise<string | null> {
    try {
      logger.debug(`Fetching ${provider} connection ID`);

      const response = await this.makeRequestWithFallback(provider, endpoint, userId);

      logger.debug(`${provider} connection response`, response.data);

      // Handle both connectionId (camelCase) and connection_id (snake_case) for compatibility
      const connectionId = response.data.connectionId || response.data.connection_id;

      if (connectionId) {
        logger.info(`Found existing ${provider} connection`, {
          connectionId,
        });
        return connectionId;
      } else {
        logger.info(`No ${provider} connection found`);
        return null;
      }
    } catch (error) {
      logger.error(`Failed to get ${provider} connection ID`, error);
      // Check localStorage as fallback in case of network issues
      const fallbackId = localStorage.getItem(`${provider}ConnectionId`);
      if (fallbackId) {
        logger.info(`Using fallback connection ID from localStorage`, { connectionId: fallbackId });
        return fallbackId;
      }
      return null;
    }
  }

  /**
   * Get Google Drive connection ID if connected
   * @returns Connection ID or null if not connected
   */
  async getGoogleDriveConnectionId(): Promise<string | null> {
    return this.getProviderConnectionId('google-drive', '/api/v1/nango/connections/google-drive');
  }

  /**
   * Get Slack connection ID if connected
   * @param userId - Optional user ID (defaults to current user)
   * @returns Connection ID or null if not connected
   */
  async getSlackConnectionId(userId?: string): Promise<string | null> {
    return this.getProviderConnectionId('slack', '/api/v1/nango/connections/slack', userId);
  }

  /**
   * Fetch Slack channels for a given connection ID
   * @param connectionId - Slack connection ID
   * @returns Promise that resolves with validated Slack channels data
   */
  async getSlackChannels(connectionId: string): Promise<SlackChannelsResponse> {
    try {
      logger.debug('Fetching Slack channels', { connectionId });
      const response = await api.get('/api/v1/nango/slack/channels', {
        params: { connection_id: connectionId },
      });

      // Validate API response with Zod schema to prevent runtime errors from API contract drift
      const validatedData = slackChannelsResponseSchema.parse(response.data);
      logger.debug('Slack channels response validated successfully', {
        channelCount: validatedData.channels.length,
      });

      return validatedData;
    } catch (error) {
      // If it's a Zod validation error, log it separately before handling as Nango error
      if (error && typeof error === 'object' && 'issues' in error) {
        logger.error('Slack channels API response validation failed', {
          error,
          connectionId,
          responseData: (error as any).data,
        });
        const validationError = new Error(
          'Invalid Slack channels API response format. Please contact support.',
        );
        (validationError as any).type = 'VALIDATION_ERROR';
        throw validationError;
      }
      this.handleNangoError('getSlackChannels', error, 'slack');
      throw error;
    }
  }

  /**
   * Get Nango connect token from backend
   * @param userId - Optional user ID (defaults to current user)
   * @param provider - Optional provider (defaults to google-drive)
   * @returns Connect session token
   */
  async getNangoConnectToken(
    userId?: string,
    provider: SupportedProvider = 'google-drive',
  ): Promise<string> {
    try {
      // Use provided user ID or get from auth context
      const effectiveUserId = userId || this.getCurrentUserId();
      logger.debug('Getting Nango connect token', { userId: effectiveUserId, provider });

      // Tokens are now in HTTP-only cookies, sent automatically with credentials: 'include'
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      const response = await fetch(`${API_BASE_URL}/api/v1/nango/connect-session-token`, {
        method: 'POST',
        headers,
        credentials: 'include', // Send HTTP-only cookies automatically
        body: JSON.stringify({
          user_id: effectiveUserId,
          end_user_id: effectiveUserId,
          provider: provider,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to get Nango connect token: ${response.statusText}`);
      }

      const data = await response.json();
      return data.connectSessionToken;
    } catch (error) {
      logger.error('Error getting Nango connect token', error);
      throw error;
    }
  }

  /**
   * Save Nango connection to backend
   * @param connectionId - Nango connection ID
   * @param provider - Provider name (optional, defaults to google-drive)
   * @returns Response data
   */
  async saveNangoConnection(
    connectionId: string,
    provider: SupportedProvider = 'google-drive',
  ): Promise<any> {
    try {
      const userId = this.getCurrentUserId();
      logger.debug('Saving Nango connection', { connectionId, userId, provider });

      const providerConfigKey = this.getProviderConfigKey(provider);

      const response = await fetch(`${API_BASE_URL}/api/v1/nango/connections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          connectionId,
          providerConfigKey: providerConfigKey,
          provider: provider,
          user_id: userId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save Nango connection: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error saving Nango connection', error);
      throw error;
    }
  }

  /**
   * Get Google Drive connection from backend
   * @returns Connection data
   */
  async getGoogleDriveConnection(): Promise<NangoConnection> {
    try {
      // Use GET with user_id as query parameter
      const effectiveUserId = this.getAuthenticatedUserId() || this.getCurrentUserId();
      const params = new URLSearchParams();
      params.append('user_id', effectiveUserId);

      const response = await api.get(`/api/v1/nango/connections/google-drive?${params.toString()}`);

      return response.data;
    } catch (error) {
      logger.error('Error getting Google Drive connection', error);
      throw error;
    }
  }

  /**
   * Open Nango Connect UI for OneDrive with enhanced error handling and event tracking
   * @param userId - User ID for the connection (optional, uses current user if not provided)
   * @returns Promise that resolves with connection details when connection is established
   */
  async connectOneDrive(
    userId?: string,
  ): Promise<{ connectionId: string; providerConfigKey: string }> {
    return this.connectProvider('one-drive', userId);
  }

  /**
   * Check if OneDrive is connected with retry logic and event tracking
   * @returns Boolean indicating if connected
   */
  async isOneDriveConnected(): Promise<boolean> {
    return (
      await this.checkProviderConnection('one-drive', '/api/v1/nango/connections/check/one-drive')
    ).connected;
  }

  /**
   * Get OneDrive connection ID if connected
   * @returns Connection ID or null if not connected
   */
  async getOneDriveConnectionId(): Promise<string | null> {
    return this.getProviderConnectionId('one-drive', '/api/v1/nango/connections/one-drive');
  }

  /**
   * Open Nango Connect UI for Dropbox with enhanced error handling and event tracking
   * @param userId - User ID for the connection (optional, uses current user if not provided)
   * @returns Promise that resolves with connection details when connection is established
   */
  async connectDropbox(
    userId?: string,
  ): Promise<{ connectionId: string; providerConfigKey: string }> {
    return this.connectProvider('dropbox', userId);
  }

  /**
   * Open Nango Connect UI for Box with enhanced error handling and event tracking
   * @param userId - User ID for the connection (optional, uses current user if not provided)
   * @returns Promise that resolves with connection details when connection is established
   */
  async connectBox(userId?: string): Promise<{ connectionId: string; providerConfigKey: string }> {
    return this.connectProvider('box', userId);
  }

  /**
   * Connect to HubSpot via Nango OAuth
   * @param userId - User ID for the connection (optional, uses current user if not provided)
   * @returns Promise that resolves with connection details when connection is established
   */
  async connectHubSpot(
    userId?: string,
  ): Promise<{ connectionId: string; providerConfigKey: string }> {
    return this.connectProvider('hubspot', userId);
  }

  /**
   * Check if HubSpot is connected with retry logic and event tracking
   * @returns Boolean indicating if connected
   */
  async isHubSpotConnected(): Promise<boolean> {
    return (
      await this.checkProviderConnection('hubspot', '/api/v1/nango/connections/check/hubspot')
    ).connected;
  }

  /**
   * Get HubSpot connection ID if connected
   * @returns Connection ID or null if not connected
   */
  async getHubSpotConnectionId(): Promise<string | null> {
    return this.getProviderConnectionId('hubspot', '/api/v1/nango/connections/check/hubspot');
  }

  /**
   * Connect to Confluence using Nango OAuth flow
   * @param userId - Optional user ID to connect for
   * @returns Connection details
   */
  async connectConfluence(
    userId?: string,
  ): Promise<{ connectionId: string; providerConfigKey: string }> {
    return this.connectProvider('confluence', userId);
  }

  /**
   * Check if Confluence is connected with retry logic and event tracking
   * @returns Boolean indicating if connected
   */
  async isConfluenceConnected(): Promise<boolean> {
    return (
      await this.checkProviderConnection('confluence', '/api/v1/nango/connections/check/confluence')
    ).connected;
  }

  /**
   * Get Confluence connection ID if connected
   * @returns Connection ID or null if not connected
   */
  async getConfluenceConnectionId(): Promise<string | null> {
    return this.getProviderConnectionId('confluence', '/api/v1/nango/connections/check/confluence');
  }

  async connectNotion(
    userId?: string,
  ): Promise<{ connectionId: string; providerConfigKey: string }> {
    return this.connectProvider('notion', userId);
  }

  async getNotionConnectionId(): Promise<string | null> {
    return this.getProviderConnectionId('notion', '/api/v1/nango/connections/notion');
  }

  async connectTrello(
    userId?: string,
  ): Promise<{ connectionId: string; providerConfigKey: string }> {
    return this.connectProvider('trello', userId);
  }

  async getTrelloConnectionId(): Promise<string | null> {
    return this.getProviderConnectionId('trello', '/api/v1/nango/connections/trello');
  }

  /**
   * Check HubSpot connection status and get connection ID in a single API call
   * This avoids duplicate API calls when both status and ID are needed
   * @returns Object with connected status and connectionId
   */
  async checkHubSpotConnectionWithId(): Promise<{
    connected: boolean;
    connectionId: string | null;
  }> {
    return this.checkProviderConnection('hubspot', '/api/v1/nango/connections/check/hubspot');
  }

  /**
   * Check if Dropbox is connected with retry logic and event tracking
   * @returns Boolean indicating if connected
   */
  async isDropboxConnected(): Promise<boolean> {
    return (
      await this.checkProviderConnection('dropbox', '/api/v1/nango/connections/check/dropbox')
    ).connected;
  }

  /**
   * Get Dropbox connection ID if connected
   * @returns Connection ID or null if not connected
   */
  async getDropboxConnectionId(): Promise<string | null> {
    return this.getProviderConnectionId('dropbox', '/api/v1/nango/connections/dropbox');
  }

  /**
   * Open Nango Connect UI for Snowflake with enhanced error handling and event tracking
   * @param userId - User ID for the connection (optional, uses current user if not provided)
   * @returns Promise that resolves with connection details when connection is established
   */
  async connectSnowflake(
    userId?: string,
  ): Promise<{ connectionId: string; providerConfigKey: string }> {
    return this.connectProvider('snowflake', userId);
  }

  /**
   * Check if Snowflake is connected with retry logic and event tracking
   * @param userId - Optional user ID to check connection for. If not provided, will try to get backend user ID.
   * @returns Boolean indicating if connected
   */
  async isSnowflakeConnected(userId?: string): Promise<boolean> {
    return (
      await this.checkProviderConnection(
        'snowflake',
        '/api/v1/nango/connections/check/snowflake',
        userId,
      )
    ).connected;
  }

  /**
   * Open Nango Connect UI for ServiceNow with enhanced error handling and event tracking
   * @param userId - User ID for the connection (optional, uses current user if not provided)
   * @returns Promise that resolves with connection details when connection is established
   */
  async connectServiceNow(
    userId?: string,
  ): Promise<{ connectionId: string; providerConfigKey: string }> {
    return this.connectProvider('servicenow', userId);
  }

  /**
   * Check if ServiceNow is connected with retry logic and event tracking
   * @param userId - Optional user ID to check connection for. If not provided, will try to get backend user ID.
   * @returns Boolean indicating if connected
   */
  async isServiceNowConnected(userId?: string): Promise<boolean> {
    return (
      await this.checkProviderConnection(
        'servicenow',
        '/api/v1/nango/connections/check/servicenow',
        userId,
      )
    ).connected;
  }

  /**
   * Get ServiceNow connection ID if connected
   * @param _userId - Optional user ID (not used, kept for API consistency)
   * @returns Connection ID or null if not connected
   */
  async getServiceNowConnectionId(userId?: string): Promise<string | null> {
    logger.debug('🔑 [nangoService] getServiceNowConnectionId called', { userId });
    const result = await this.getProviderConnectionId(
      'servicenow',
      '/api/v1/nango/connections/servicenow',
      userId,
    );
    logger.debug('🔑 [nangoService] getServiceNowConnectionId result', { result });
    return result;
  }

  /**
   * Get Snowflake connection ID if connected
   * @param userId - Optional user ID (not used, kept for API consistency)
   * @returns Connection ID or null if not connected
   */
  async getSnowflakeConnectionId(userId?: string): Promise<string | null> {
    logger.debug('🔑 [nangoService] getSnowflakeConnectionId called', { userId });
    const result = await this.getProviderConnectionId(
      'snowflake',
      '/api/v1/nango/connections/check/snowflake',
      userId,
    );
    logger.debug('🔑 [nangoService] getSnowflakeConnectionId result', { result });
    return result;
  }

  /**
   * Check Snowflake connection status and get connection ID in a single API call
   * This avoids duplicate API calls when both status and ID are needed
   * @param userId - Optional user ID to check connection for
   * @returns Object with connected status and connectionId
   */
  async checkSnowflakeConnectionWithId(
    userId?: string,
  ): Promise<{ connected: boolean; connectionId: string | null }> {
    return this.checkProviderConnection(
      'snowflake',
      '/api/v1/nango/connections/check/snowflake',
      userId,
    );
  }

  /**
   * Open Nango Connect UI for Google Cloud Storage with enhanced error handling and event tracking
   * @param userId - User ID for the connection (optional, uses current user if not provided)
   * @returns Promise that resolves with connection details when connection is established
   */
  async connectGoogleCloudStorage(
    userId?: string,
  ): Promise<{ connectionId: string; providerConfigKey: string }> {
    return this.connectProvider('google-cloud-storage', userId);
  }

  /**
   * Open Nango Connect UI for Zoho CRM with enhanced error handling and event tracking
   * @param userId - User ID for the connection (optional, uses current user if not provided)
   * @returns Promise that resolves with connection details when connection is established
   */
  async connectZoho(userId?: string): Promise<{ connectionId: string; providerConfigKey: string }> {
    return this.connectProvider('zoho', userId);
  }

  /**
   * Check if Zoho CRM is connected with retry logic and event tracking
   * @param userId - Optional user ID to check connection for. If not provided, will try to get backend user ID.
   * @returns Boolean indicating if connected
   */
  async isZohoConnected(userId?: string): Promise<boolean> {
    return (
      await this.checkProviderConnection('zoho', '/api/v1/nango/connections/check/zoho', userId)
    ).connected;
  }

  /**
   * Get Zoho CRM connection ID if connected
   * @param userId - Optional user ID (not used, kept for API consistency)
   * @returns Connection ID or null if not connected
   */
  async getZohoConnectionId(userId?: string): Promise<string | null> {
    logger.debug('🔑 [nangoService] getZohoConnectionId called', { userId });
    const result = await this.getProviderConnectionId(
      'zoho',
      '/api/v1/nango/connections/zoho',
      userId,
    );
    logger.debug('🔑 [nangoService] getZohoConnectionId result', { result });
    return result;
  }

  /**
   * Open Nango Connect UI for QuickBooks with enhanced error handling and event tracking
   * @param userId - User ID for the connection (optional, uses current user if not provided)
   * @returns Promise that resolves with connection details when connection is established
   */
  async connectQuickBooks(
    userId?: string,
  ): Promise<{ connectionId: string; providerConfigKey: string }> {
    return this.connectProvider('quickbooks', userId);
  }

  /**
   * Open Nango Connect UI for SignNow with enhanced error handling and event tracking
   * @param userId - User ID for the connection (optional, uses current user if not provided)
   * @returns Promise that resolves with connection details when connection is established
   */
  async connectSignNow(
    userId?: string,
  ): Promise<{ connectionId: string; providerConfigKey: string }> {
    return this.connectProvider('signnow', userId);
  }

  /**
   * Connect to Slack
   * @param userId - Optional user ID (defaults to current user)
   * @returns Promise that resolves with connection details when connection is established
   */
  async connectSlack(
    userId?: string,
  ): Promise<{ connectionId: string; providerConfigKey: string }> {
    return this.connectProvider('slack', userId);
  }

  /**
   * Check if Google Cloud Storage is connected with retry logic and event tracking
   * @param userId - Optional user ID to check connection for. If not provided, will try to get backend user ID.
   * @returns Boolean indicating if connected
   */
  async isGoogleCloudStorageConnected(userId?: string): Promise<boolean> {
    return (
      await this.checkProviderConnection(
        'google-cloud-storage',
        '/api/v1/nango/connections/check/google-cloud-storage',
        userId,
      )
    ).connected;
  }

  /**
   * Get Google Cloud Storage connection ID if connected
   * @param userId - Optional user ID (not used, kept for API consistency)
   * @returns Connection ID or null if not connected
   */
  async getGoogleCloudStorageConnectionId(userId?: string): Promise<string | null> {
    logger.debug('🔑 [nangoService] getGoogleCloudStorageConnectionId called', { userId });
    const result = await this.getProviderConnectionId(
      'google-cloud-storage',
      '/api/v1/nango/connections/google-cloud-storage',
      userId,
    );
    logger.debug('🔑 [nangoService] getGoogleCloudStorageConnectionId result', { result });
    return result;
  }

  /**
   * Check if SignNow is connected with retry logic and event tracking
   * @param userId - Optional user ID to check connection for. If not provided, will try to get backend user ID.
   * @returns Boolean indicating if connected
   */
  async isSignNowConnected(userId?: string): Promise<boolean> {
    return (
      await this.checkProviderConnection(
        'signnow',
        '/api/v1/nango/connections/check/signnow',
        userId,
      )
    ).connected;
  }

  /**
   * Get SignNow connection ID if connected
   * @param userId - Optional user ID (not used, kept for API consistency)
   * @returns Connection ID or null if not connected
   */
  async getSignNowConnectionId(userId?: string): Promise<string | null> {
    logger.debug('🔑 [nangoService] getSignNowConnectionId called', { userId });
    const result = await this.getProviderConnectionId(
      'signnow',
      '/api/v1/nango/connections/signnow',
      userId,
    );
    logger.debug('🔑 [nangoService] getSignNowConnectionId result', { result });
    return result;
  }

  /**
   * Save connection information to database with retry logic
   * @param connectionId - Nango connection ID
   * @param providerConfigKey - Provider config key
   * @param userId - User ID for the connection
   * @param provider - Provider name
   */
  private async saveConnection(
    connectionId: string,
    providerConfigKey: string,
    userId: string,
    provider: SupportedProvider = 'google-drive',
  ): Promise<void> {
    try {
      await withRetry(
        async () => {
          // Use authenticated user ID from JWT token instead of OAuth user ID
          const authenticatedUserId = this.getAuthenticatedUserId();
          const effectiveUserId = authenticatedUserId || userId;

          logger.debug('Saving connection to database', {
            connectionId,
            providerConfigKey,
            userId: effectiveUserId,
            provider,
            oauthUserId: userId,
            authenticatedUserId: authenticatedUserId,
          });

          const response = await api.post('/api/v1/nango/connections', {
            connectionId,
            providerConfigKey,
            provider: provider,
            user_id: effectiveUserId, // Use authenticated user ID
          });

          logger.info('Connection saved successfully', {
            connectionId,
            provider,
            userId: effectiveUserId,
            responseStatus: response.status,
          });
        },
        nangoRetryConfig,
        (attempt, delay, error) => {
          logger.warn(`Retrying saveConnection (attempt ${attempt})`, {
            delay,
            error: error instanceof Error ? error.message : String(error),
            provider,
          });
        },
      );
    } catch (error) {
      logger.error('Failed to save connection', {
        error,
        connectionId,
        provider,
        userId: userId,
      });

      // Don't throw here - connection is established, saving is secondary
    }
  }

  /**
   * Get available provider configurations from Nango
   * @returns Object containing available and unavailable provider lists
   */
  async getAvailableProviderConfigs(): Promise<AvailableProviderConfigsResponse> {
    try {
      logger.debug('Fetching available provider configs from Nango');
      const response = await api.get('/api/v1/nango/provider-configs/available');

      // Validate API response with Zod schema to prevent runtime errors from API contract drift
      const validatedData = availableProviderConfigsResponseSchema.parse(response.data);
      logger.debug('Available provider configs response validated successfully', {
        available: validatedData.available_providers.length,
        unavailable: validatedData.unavailable_providers.length,
      });

      return validatedData;
    } catch (error) {
      // If it's a Zod validation error, log it separately before handling as Nango error
      if (error && typeof error === 'object' && 'issues' in error) {
        logger.error('Available provider configs API response validation failed', {
          error,
          responseData: (error as any).data,
        });
        const validationError = new Error(
          'Invalid available provider configs API response format. Please contact support.',
        );
        (validationError as any).type = 'VALIDATION_ERROR';
        throw validationError;
      }
      logger.error('Failed to fetch available provider configs', { error });
      // For provider config errors, we don't have a specific provider, so use default
      this.handleNangoError('getAvailableProviderConfigs', error);
      throw error;
    }
  }
}
